import { useDl } from "../utils/dlToken";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, ChevronDown, Paperclip, Check, X } from "lucide-react";
import { PROF_T } from "../content/professions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = PROF_T.sk;
const ST = { pending: "Čaká na posúdenie", approved: "Schválená", rejected: "Zamietnutá" };

function Row({ r, token, onDone }) {
  const dl = useDl(token);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (approve) => {
    if (!window.confirm(approve ? `Schváliť úroveň ${P.levels[r.new_level]}? Uchádzačovi odíde e-mail.` : "Zamietnuť žiadosť? Uchádzačovi odíde e-mail.")) return;
    setBusy(true);
    try { await axios.post(`${API}/admin/level-requests/${r.id}/decide`, { approve, note }, { headers: { Authorization: `Bearer ${token}` } }); onDone(); }
    catch (e) { alert(e?.response?.data?.detail || "Chyba"); } finally { setBusy(false); }
  };
  const prof = r.profession === "other" ? r.profession_other || P.names.other : P.names[r.profession] || r.profession;
  return (
    <div className="border border-white/10 p-4" data-testid={`admin-level-request-${r.id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div>
          <p className="text-white">{r.name} <span className="text-white/40 text-xs">{r.email}</span></p>
          <p className="text-[#D4AF37] text-xs font-mono">{r.certificate_no}</p>
          <p className="text-sm text-white/80 mt-1">{prof}: {P.levels[r.current_level] || "—"} → <strong>{P.levels[r.new_level]}</strong> · 50 € {r.paid ? "uhradené" : ""}</p>
        </div>
        <span className="text-xs text-white/50" data-testid={`admin-level-status-${r.id}`}>{ST[r.status] || r.status} · {new Date(r.created_at).toLocaleDateString("sk-SK")}</span>
      </div>
      {r.note && <p className="text-white/55 text-sm mt-2">{r.note}</p>}
      <div className="flex flex-wrap gap-3 mt-2">
        {(r.files || []).map((f) => (
          <a key={f.id} href={`${API}/files/${f.id}/download?token=${dl}`} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] inline-flex items-center gap-1 hover:underline" data-testid={`admin-level-file-${f.id}`}><Paperclip size={12} /> {f.original_filename}</a>
        ))}
      </div>
      {r.status === "pending" ? (
        <div className="flex flex-wrap gap-2 mt-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Poznámka pre uchádzača (nepovinné)" className="flex-1 min-w-[200px] bg-black border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#D4AF37]" data-testid={`admin-level-note-${r.id}`} />
          <button disabled={busy} onClick={() => decide(true)} className="inline-flex items-center gap-1 bg-emerald-600 text-white px-4 py-2 text-sm disabled:opacity-50" data-testid={`admin-level-approve-${r.id}`}><Check size={14} /> Schváliť</button>
          <button disabled={busy} onClick={() => decide(false)} className="inline-flex items-center gap-1 border border-red-400/60 text-red-300 px-4 py-2 text-sm disabled:opacity-50" data-testid={`admin-level-reject-${r.id}`}><X size={14} /> Zamietnuť</button>
        </div>
      ) : r.admin_note && <p className="text-white/40 text-xs mt-2">Poznámka: {r.admin_note}</p>}
    </div>
  );
}

export default function AdminLevelRequests({ token }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(true);
  const load = useCallback(() => axios.get(`${API}/admin/level-requests`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setRows(r.data.rows)).catch(() => setRows([])), [token]);
  useEffect(() => { load(); }, [load]);
  const pending = (rows || []).filter((r) => r.status === "pending").length;
  return (
    <div className="border border-[#D4AF37]/30 bg-[#0a0a0a] mb-8" data-testid="admin-level-requests">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-4" data-testid="admin-level-requests-toggle">
        <span className="flex items-center gap-2 font-display text-lg"><TrendingUp size={17} className="text-[#D4AF37]" /> Žiadosti o vyššiu úroveň</span>
        <span className="flex items-center gap-3 text-xs text-white/50">čaká {pending} / spolu {(rows || []).length} <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          {rows && rows.length === 0 && <p className="text-white/40 text-sm" data-testid="admin-level-requests-empty">Zatiaľ žiadne žiadosti.</p>}
          {(rows || []).map((r) => <Row key={r.id} r={r} token={token} onDone={load} />)}
        </div>
      )}
    </div>
  );
}
